import { useEffect, useState } from "react";
import { useTonWallet } from "@tonconnect/ui-react";
import { rpcCall } from "../ws/client";
import { DealRequest, Order } from "../ws/types";
import { useQAProfile, isLocalhost } from "../hooks/useQAProfile";
import "../styles/DealRequests.scss";

export default function MyDealRequests() {
    const wallet = useTonWallet();
    const { isQAMode, profile, buyerWallet, toggleQA, switchProfile, currentWallet: qaWallet } = useQAProfile(wallet?.account?.address);
    
    const effectiveWallet = qaWallet || wallet?.account?.address || "";
    const [requests, setRequests] = useState<DealRequest[]>([]);
    const [orders, setOrders] = useState<Map<string, Order>>(new Map());
    const [loading, setLoading] = useState(false);
    const [cancellingId, setCancellingId] = useState<string | null>(null);
    const [statusFilter, setStatusFilter] = useState<string>("pending");
    const [filteredRequests, setFilteredRequests] = useState<DealRequest[]>([]);

    useEffect(() => {
        if (effectiveWallet) {
            loadRequests();
        }
    }, [effectiveWallet, profile]);

    useEffect(() => {
        // Apply status filter
        if (statusFilter === "all") {
            setFilteredRequests(requests);
        } else {
            setFilteredRequests(requests.filter(r => r.status === statusFilter));
        }
    }, [requests, statusFilter]);

    const loadRequests = async () => {
        if (!effectiveWallet) return;
        
        // In QA mode, only show outgoing requests when profile is 'buyer'
        if (isQAMode && profile !== 'buyer') {
            setRequests([]);
            setFilteredRequests([]);
            setOrders(new Map());
            return;
        }

        setLoading(true);
        try {
            const response = await rpcCall<
                { requester: string },
                { requests: DealRequest[] }
            >("deal_requests.list", {
                requester: effectiveWallet
            });

            if (response.result?.requests) {
                setRequests(response.result.requests);
                setFilteredRequests(response.result.requests);
                
                // Load order info for each request
                const orderMap = new Map<string, Order>();
                for (const req of response.result.requests) {
                    const orderResp = await rpcCall<
                        { order_id: string },
                        { order: Order }
                    >("orders.get", { order_id: req.order_id });
                    
                    if (orderResp.result?.order) {
                        orderMap.set(req.order_id, orderResp.result.order);
                    }
                }
                setOrders(orderMap);
            }
        } catch (err) {
            console.error("Failed to load requests:", err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = async (requestId: string) => {
        if (!effectiveWallet) return;

        if (!confirm("Вы уверены, что хотите отменить эту заявку?")) {
            return;
        }

        setCancellingId(requestId);
        try {
            const response = await rpcCall(
                "deal_requests.cancel",
                {
                    request_id: requestId,
                    requester: effectiveWallet
                }
            );

            if (response.error) {
                console.error("Cancel error:", response.error);
                alert(response.error.message);
            } else {
                alert("Заявка отменена");
                loadRequests();
            }
        } catch (err) {
            console.error("Cancel exception:", err);
            alert("Ошибка при отмене заявки");
        } finally {
            setCancellingId(null);
        }
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case "pending":
                return <span className="badge badge-pending">Ожидает</span>;
            case "accepted":
                return <span className="badge badge-success">Принята</span>;
            case "rejected":
                return <span className="badge badge-danger">Отклонена</span>;
            case "cancelled":
                return <span className="badge badge-secondary">Отменена</span>;
            default:
                return <span className="badge">{status}</span>;
        }
    };

    if (!wallet && !isQAMode) {
        return (
            <div className="deal-requests">
                <h2>Мои заявки</h2>
                <div className="empty-state">
                    <p>Подключите кошелёк для просмотра заявок</p>
                </div>
            </div>
        );
    }

    return (
        <div className="deal-requests">
            {/* QA Mode Controls */}
            {isQAMode && (
                <div className="qa-controls">
                    <div className="qa-toggle">
                        <label className="qa-switch">
                            <input 
                                type="checkbox" 
                                checked={isQAMode}
                                onChange={toggleQA}
                            />
                            <span className="qa-slider"></span>
                        </label>
                        <span className="qa-label">🔧 QA Mode</span>
                    </div>
                    <div className="qa-profile">
                        <span className={`profile-badge ${profile}`}>
                            {profile === 'seller' ? '🏪 Продавец' : '🛒 Покупатель'}
                        </span>
                        <button className="btn btn-small" onClick={switchProfile}>
                            🔄 Сменить профиль
                        </button>
                    </div>
                </div>
            )}

            <div className="header">
                <h2>Мои заявки</h2>
                <div className="header-actions">
                    {!isQAMode && isLocalhost() && (
                        <label className="qa-toggle-inline">
                            <input 
                                type="checkbox" 
                                checked={isQAMode}
                                onChange={toggleQA}
                            />
                            🔧 QA Mode
                        </label>
                    )}
                    <select 
                        className="filter-select"
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                    >
                        <option value="all">Все статусы</option>
                        <option value="pending">Ожидающие</option>
                        <option value="accepted">Принятые</option>
                        <option value="rejected">Отклонённые</option>
                        <option value="cancelled">Отменённые</option>
                    </select>
                    <button className="btn btn-secondary" onClick={loadRequests} disabled={loading}>
                        {loading ? "Загрузка..." : "Обновить"}
                    </button>
                </div>
            </div>

            {loading && requests.length === 0 && <p>Загрузка...</p>}

            {!loading && filteredRequests.length === 0 && (
                <div className="empty-state">
                    <p>У вас нет заявок с выбранным фильтром</p>
                </div>
            )}

            <div className="requests-list">
                {filteredRequests.map((request) => {
                    const order = orders.get(request.order_id);
                    if (!order) return null;

                    return (
                        <div key={request.id} className="request-card">
                            <div className="request-header">
                                {getStatusBadge(request.status)}
                                <span className="time">
                                    {new Date(request.created_at).toLocaleString("ru-RU")}
                                </span>
                            </div>

                            <div className="order-info">
                                <h3>
                                    {order.side === "sell" ? "Продажа" : "Покупка"}{" "}
                                    {request.amount || order.amount} {order.jetton_symbol}
                                </h3>
                                <div className="details">
                                    <div className="detail-row">
                                        <span className="label">Цена:</span>
                                        <span className="value">
                                            {order.price} {order.fiat_currency}
                                        </span>
                                    </div>
                                    <div className="detail-row">
                                        <span className="label">Итого:</span>
                                        <span className="value">
                                            {((request.amount || order.amount) * order.price).toFixed(2)} {order.fiat_currency}
                                        </span>
                                    </div>
                                </div>
                            </div>

                            {request.message && (
                                <div className="requester-info">
                                    <div className="message">
                                        <strong>Ваше сообщение:</strong>
                                        <p>{request.message}</p>
                                    </div>
                                </div>
                            )}

                            {request.status === "rejected" && request.rejection_reason && (
                                <div className="rejection-info">
                                    <strong>Причина отклонения:</strong>
                                    <p>{request.rejection_reason}</p>
                                </div>
                            )}

                            {request.status === "pending" && (
                                <div className="request-actions">
                                    <button
                                        className="btn btn-danger btn-outline"
                                        onClick={() => handleCancel(request.id)}
                                        disabled={cancellingId === request.id}
                                    >
                                        {cancellingId === request.id ? "Отмена..." : "Отменить заявку"}
                                    </button>
                                </div>
                            )}

                            {request.status === "accepted" && (
                                <div className="accepted-info">
                                    <p>✅ Заявка принята! Перейдите во вкладку "Мои сделки"</p>
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
